import { Waypoint } from 'react-waypoint'
import { defineMessages, useIntl } from 'react-intl'

import { TrackingEventConstants } from '../../../../constants/tracking'

// Components
import { Format, Lock, Text } from '../../..'
import { LockableInlineCurrencyText } from '../../CopyHelpers'
import PieChart from '../../PieChart'
import { YourLoanSentence } from '../../../homeowners/YourLoanSentence/YourLoanSentence'

// Helpers
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'

// Styles
import styles from './PaidSoFar.module.scss'
import { sampleLoan } from '../../../../constants/homePreview'
import { useSelector } from 'react-redux'
import {
  selectPrincipalDiff,
  selectTotalInterestPaidOnAllCurrentCELs,
  selectTotalPrincipalPaidOnAllCurrentCELs
} from '../../../../store/selectors/home/loans'
import { selectShouldCheckLoanNumbers } from '../../../../store/selectors/home/homeowner'
import { useTrackingContext } from '../../../../providers/tracking'

interface PaidSoFarProps {
  withCurrentHomeData: boolean // not in Preview mode
}

export const PaidSoFar = ({ withCurrentHomeData }: PaidSoFarProps) => {
  if (withCurrentHomeData) {
    return <PaidSoFarDigest />
  } else {
    return <PaidSoFarPreview />
  }
}

const PaidSoFarDigest = () => {
  const principal = useSelector(selectTotalPrincipalPaidOnAllCurrentCELs)
  const interest = useSelector(selectTotalInterestPaidOnAllCurrentCELs)
  const total = principal + interest
  const emptyState = total === 0
  const shouldCheckNumbers = useSelector(selectShouldCheckLoanNumbers)
  const principalDiff = useSelector(selectPrincipalDiff)

  const digestProps: PaidSoFarBaseProps = {
    withCurrentHomeData: true,
    shouldCheckNumbers: shouldCheckNumbers,
    principal: Math.round(principal),
    principalDiff: principalDiff,
    principalRatio: principal / total,
    interest: Math.round(interest),
    interestRatio: interest / total,
    emptyState: emptyState
  }
  if (emptyState && !shouldCheckNumbers) {
    return null
  } else {
    return <PaidSoFarBase {...digestProps} />
  }
}

const PaidSoFarPreview = () => {
  const { principalPaid: principal, principalDiff, interestPaid: interest } = sampleLoan.progress
  const total = principal + interest

  const previewProps: PaidSoFarBaseProps = {
    withCurrentHomeData: false,
    shouldCheckNumbers: false,
    principal: Math.round(principal),
    principalDiff,
    principalRatio: principal / total,
    interest: Math.round(interest),
    interestRatio: interest / total,
    emptyState: false
  }

  return <PaidSoFarBase {...previewProps} />
}

export const MSG = defineMessages({
  emptyState: {
    id: 'Homeowner.interest.noInterestYet',
    defaultMessage: 'Confirming your loan data is the first step towards smart homeownership.'
  },
  towardsPrincipal: {
    id: 'Homeowner.interest.towardsPrincipal',
    defaultMessage: 'Towards Principal'
  },
  towardsInterest: {
    id: 'Homeowner.interest.towardsInterest',
    defaultMessage: 'Towards Interest'
  },
  towardsPrincipalExplain: {
    id: 'Homeowner.interest.towardsPrincipal.explain',
    defaultMessage: 'How much of your loan you have repaid.'
  },
  towardsInterestExplain: {
    id: 'Homeowner.interest.towardsInterest.explain',
    defaultMessage: 'What the lender charges you for your loan.'
  },
  towardsPrincipalExplainWithNumbers: {
    id: 'Homeowner.interest.towardsPrincipal.explainWithNumbers',
    defaultMessage: "{originalPrincipal} + {principalDiff} extra you've paid ahead of time."
  }
})

interface PaidSoFarBaseProps {
  withCurrentHomeData: boolean
  interest: number
  interestRatio: number
  principal: number
  principalRatio: number
  principalDiff: number
  shouldCheckNumbers: boolean
  emptyState: boolean
}

const PaidSoFarBase = ({
  withCurrentHomeData,
  interest,
  interestRatio,
  principal,
  principalRatio,
  principalDiff,
  shouldCheckNumbers,
  emptyState
}: PaidSoFarBaseProps) => {
  const { trackingClient } = useTrackingContext()
  const intl = useIntl()
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        module_name: TrackingEventConstants.PRINCIPAL_INTEREST
      })
  )

  const handleTrackModuleViewed = () => {
    trackModuleViewed()
  }

  return (
    <>
      {emptyState ? (
        <Text
          variant='neutralCool'
          variantCount='900'
          align='center'
          className={styles.noInterestYet}
          size='s'
          data-qa='paidSoFar.unverifiedHeadline'
        >
          {intl.formatMessage(MSG.emptyState)}
        </Text>
      ) : (
        <div data-qa='paidSoFar.body'>
          <div className={styles.chartContainer}>
            <div className={styles.leftFact}>
              <Text element='h3' size='l' weight='light' variant='neutralCool' variantCount='900'>
                {intl.formatMessage(MSG.towardsPrincipal)}
              </Text>
              <Text
                className={styles.number}
                size='xxl'
                weight='light'
                variant='success'
                element='div'
                data-qa='paidSoFar.principal'
              >
                <Lock enabled={!withCurrentHomeData}>
                  <Format.Currency value={principal} />
                </Lock>
              </Text>
              <Text variant='neutralCool' variantCount='700' size='xs'>
                {principalDiff > 0 ? (
                  <>
                    {intl.formatMessage(MSG.towardsPrincipalExplainWithNumbers, {
                      originalPrincipal: LockableInlineCurrencyText({
                        currency: principal - principalDiff,
                        variant: 'primary',
                        locked: !withCurrentHomeData
                      }),
                      principalDiff: LockableInlineCurrencyText({
                        currency: principalDiff,
                        variant: 'success',
                        locked: !withCurrentHomeData
                      })
                    })}
                  </>
                ) : (
                  <>{intl.formatMessage(MSG.towardsPrincipalExplain)}</>
                )}
              </Text>
            </div>

            <div className={styles.chart}>
              <PieChart
                slices={[
                  {
                    value: interestRatio,
                    key: 'interestRatio',
                    className: styles.interestSlice
                  },
                  {
                    offset: true,
                    value: principalRatio,
                    key: 'principalRatio',
                    className: styles.principalSlice
                  }
                ]}
              />
            </div>

            <div className={styles.rightFact}>
              <Text element='h3' size='l' weight='light' variant='neutralCool' variantCount='900'>
                {intl.formatMessage(MSG.towardsInterest)}
              </Text>
              <Text
                className={styles.number}
                size='xxl'
                weight='light'
                variant='danger'
                element='div'
                data-qa='paidSoFar.interest'
              >
                <Lock enabled={!withCurrentHomeData}>
                  <Format.Currency value={interest} />
                </Lock>
              </Text>
              <Text variant='neutralCool' variantCount='700' size='xs'>
                {intl.formatMessage(MSG.towardsInterestExplain)}
              </Text>
            </div>
          </div>
          {!shouldCheckNumbers && withCurrentHomeData && (
            <Text
              size='xs'
              variant='neutralCool'
              variantCount='700'
              className={styles.loanSentence}
              data-qa='paidSoFar.descriptor'
            >
              <YourLoanSentence />
            </Text>
          )}
        </div>
      )}

      {withCurrentHomeData && (
        <Waypoint onEnter={handleTrackModuleViewed} bottomOffset='25%'>
          <div />
        </Waypoint>
      )}
    </>
  )
}
